import React, { useEffect, useMemo, useRef, useState } from "react";
import { Grid } from "@mui/material";
import Loader from "../../../library/common/Loader";
import { useReactToPrint } from "react-to-print";
import "./viewInspectionReport.scss";
import ReportHeader from "./common/reportHeader/reportHeader";
import BasicInformation from "./common/basicInformation/basicInformation";
import "./common/common.scss";
import * as viewInspectionAction from "../../../redux/actions/incpectionAction";
import { formatToMMDDYYYY, sanitizeFileName } from "../../../utils/helper";

import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  HBNEXT_ORG_ID,
  LOGIN_ORG_ID,
  ORG_ID,
} from "../../../constant/constant";
import CategoriesReportView from "./common/categoriesReportView/categoriesReportView/categoriesReportView";
import { checkPrivileges } from "../../../utils/rolesHelper";
import {
  ACCESS_DENIED,
  VIEW_INSPECTION_REPORT,
} from "../../../constant/routeContant";
import commonImages from "../../../assets";
import BackBtn from "../../../library/custom/backBtn/backBtn";

// The report is only printable once every image inside it has painted, so the
// rendered container is scanned for work still in flight. Images keep arriving
// after the first paint (report data, HEIC conversions), so the scan reruns on
// every DOM change inside the container.
const useReportImagesLoaded = (containerRef, active) => {
  const [imagesLoaded, setImagesLoaded] = useState(false);

  useEffect(() => {
    if (!active) {
      setImagesLoaded(false);
      return;
    }
    const container = containerRef.current;
    if (!container) return;

    const listeners = [];
    const tracked = new WeakSet();
    let cancelled = false;
    let runId = 0;

    // complete covers both a painted image and one that failed to decode, so a
    // broken url settles instead of blocking the button forever.
    const isSettled = (img) => !img.getAttribute("src") || img.complete;

    // A tile still fetching or converting (HEIC) renders its spinner and has no
    // <img> to wait on yet, so the spinner itself counts as outstanding work.
    const hasPendingTile = () => !!container.querySelector(".imageLoader");

    const track = (img) => {
      if (tracked.has(img)) return;
      tracked.add(img);
      // A deferred image never fires load while it is off screen, which would
      // keep the button disabled and leave the image blank in the PDF.
      if (img.loading === "lazy") img.loading = "eager";
      const onSettled = () => scan();
      img.addEventListener("load", onSettled);
      img.addEventListener("error", onSettled);
      listeners.push([img, onSettled]);
    };

    const scan = () => {
      if (cancelled) return;
      const currentRun = ++runId;
      const images = Array.from(container.querySelectorAll("img"));
      images.forEach(track);

      if (hasPendingTile() || images.some((img) => !isSettled(img))) {
        setImagesLoaded(false);
        return;
      }

      // load only means the bytes are ready. Decoding every image and then
      // waiting two frames means the browser has actually painted them, and
      // anything that mounts in between starts a newer run that wins.
      Promise.all(
        images.map((img) =>
          typeof img.decode === "function" ? img.decode().catch(() => {}) : null,
        ),
      ).then(() => {
        if (cancelled || currentRun !== runId) return;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (cancelled || currentRun !== runId || hasPendingTile()) return;
            setImagesLoaded(true);
          });
        });
      });
    };

    const observer = new MutationObserver(scan);
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["src"],
    });
    scan();

    return () => {
      cancelled = true;
      observer.disconnect();
      listeners.forEach(([img, handler]) => {
        img.removeEventListener("load", handler);
        img.removeEventListener("error", handler);
      });
    };
  }, [containerRef, active]);

  return imagesLoaded;
};

const ViewInspectionReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();
  const reportId = location?.state?.reportId;

  const getInspectionDetails = useSelector(
    (state) => state?.inspectionDetailData?.inspEditDetails,
  );

  const queryParams = new URLSearchParams(window.location.search);
  const paramInspectionId =
    queryParams.get("InspectionId") || queryParams.get("id");
  const paramOrgId = queryParams.get("organisationId") || ORG_ID();

  const showLoader = useSelector(
    (state) =>
      state?.inspectionDetailData?.isInLoading ||
      state?.getInspectionAcknowledgement?.isInLoading,
  );

  const getInspectionReport = (data) => {
    dispatch(viewInspectionAction.getInspectionDetails({ data }));
  };

  const defaultReportData = {
    InspectionId: reportId,
    organisationId: ORG_ID(),
  };

  useEffect(() => {
    if (reportId) {
      getInspectionReport(defaultReportData);
    }
  }, [reportId]);

  const [pdfLoader, setPdfLoader] = useState(false);
  const componentRef = useRef(null);

  const reportFileName = [
    "Inspection Report",
    sanitizeFileName(getInspectionDetails?.asset),
    formatToMMDDYYYY(getInspectionDetails?.conducted_On),
  ]
    .filter(Boolean)
    .join(" ");

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: reportFileName,
    fonts: ["Lato"],
    bodyClass: "bodyPdf",
    onBeforeGetContent: () => setPdfLoader(true),
    onAfterPrint: () => setPdfLoader(false),
    onPrintError: () => setPdfLoader(false),
  });

  const imagesLoaded = useReportImagesLoaded(componentRef, !showLoader);
  const disablePrint = showLoader || pdfLoader || !imagesLoaded;

  const headerData = useMemo(
    () => ({
      report_Id: getInspectionDetails?.report_Id,
      conducted_On: getInspectionDetails?.conducted_On,
      inspectionFormName: getInspectionDetails?.questionSet,
    }),
    [getInspectionDetails],
  );

  const basicInformationData = useMemo(
    () => ({
      inspected_By: getInspectionDetails?.inspected_By,
      dateOfIns: getInspectionDetails?.dateOfIns,
      timeOfIns: getInspectionDetails?.timeOfIns,
      location: getInspectionDetails?.location,
      projectName: getInspectionDetails?.projectName,
      assetType: getInspectionDetails?.assetType,
      asset: getInspectionDetails?.asset,
      weatherTimeofIns: getInspectionDetails?.weatherTimeofIns,
      generalComment: getInspectionDetails?.generalComment,
    }),
    [getInspectionDetails],
  );

  const headerFields = useMemo(
    () =>
      getInspectionDetails?.headerFieldsInfoResponse
        ?.filter((field) => field?.isSelected)
        ?.sort((a, b) => a.sequenceNumber - b.sequenceNumber),
    [getInspectionDetails],
  );

  const hasAccess = useMemo(
    () => checkPrivileges([12, 120, 121, 122, 4, 6, 5]),
    [],
  );

  const isClient = ORG_ID() !== LOGIN_ORG_ID();
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const clientLogo = localStorage.getItem("orgLogo");
  const clientName = localStorage.getItem("orgName");
  //const logoCheck = isClient ? clientLogo : userInfo?.logo;
  const logoCheck =
    isClient && clientLogo && clientLogo !== "undefined"
      ? clientLogo
      : isClient && clientLogo === "undefined"
        ? commonImages?.hbNextLogo
        : parseInt(ORG_ID()) === parseInt(HBNEXT_ORG_ID)
          ? commonImages?.hbNextLogo
          : userInfo?.logo
            ? userInfo?.logo
            : commonImages?.hbNextLogo;

  const { weather } = useSelector((state) => state.inspection);

  useEffect(() => {
    dispatch(viewInspectionAction.getReferenceData());
  }, []);

  useEffect(() => {
    if (paramInspectionId && paramOrgId) {
      const defaultParamReportData = {
        InspectionId: parseInt(paramInspectionId),
        organisationId: parseInt(paramOrgId) ? parseInt(paramOrgId) : ORG_ID(),
      };
      getInspectionReport(defaultParamReportData);
    }
  }, [paramInspectionId, paramOrgId]);

  const dataBack = {
    title: "Back",
    route: VIEW_INSPECTION_REPORT,
    state: {
      assetType: location?.state?.filters?.assetTypeId || "",
      fromDate: location?.state?.filters?.fromDate,
      toDate: location?.state?.filters?.toDate,
      status: location?.state?.filters?.statusId || "",
      divisionId: location?.state?.filters?.divisionId || 0,
      inspectionType: location?.state?.filters?.inspectionType || "",
      assetId: location?.state?.filters?.assetId || "",
      assetValue: location?.state?.filters?.assetValue || "",
      assetCatId: location?.state?.filters?.assetCategoryId || "",
      search: location?.state?.search || "",
      pageNo: location?.state?.page || 1,
      pageSize: location?.state?.pageSize || 10,
      routeName: location?.state?.routeName || "",
      userId: location?.state?.userId || "",
      apiData: location?.state?.drillDetail?.apiData,
      tab: location?.state?.tab,
      backpage: location?.state?.backpage,
      drillDetail: location?.state?.drillDetail,
      reportDetails: location?.state?.reportDetails,
      tab: 0,
      page: "dashboardIns",
    },
  };

  return (
    <>
      {hasAccess ? (
        <>
          {pdfLoader && <Loader />}

          {showLoader ? (
            <Loader />
          ) : (
            <Grid
              mt={0}
              ref={componentRef}
              className="InspectionContainer overRideSaftyPdf"
            >
              <Grid container>
                <Grid item md={12} className="hideinpdf">
                  <BackBtn dataBack={dataBack} className="equipmentBack" />
                </Grid>
                <Grid item md={12}>
                  <div className="title repotPdfHeading">
                    View Generated Report
                  </div>
                </Grid>
                <Grid item md={12} className="clientPdfDetail">
                  <Grid className="clientName">
                    {isClient ? clientName : userInfo?.clientName}
                  </Grid>
                  <Grid className="clientLogo">
                    {logoCheck && <img src={logoCheck} alt="client logo" />}
                  </Grid>
                </Grid>
              </Grid>

              <ReportHeader
                handlePrint={handlePrint}
                headerData={headerData}
                logoCheck={logoCheck}
                disablePrint={disablePrint}
              />
              <BasicInformation
                weather={weather}
                basicInformationData={basicInformationData}
                headerFields={headerFields}
              />
              <CategoriesReportView
                viewDetail={getInspectionDetails}
                SIGNATUREURL={getInspectionDetails?.signature}
                getInspectionDetails={getInspectionDetails}
              />
            </Grid>
          )}
        </>
      ) : (
        navigate(ACCESS_DENIED)
      )}
    </>
  );
};

export default ViewInspectionReport;
